/**
 * Tone samples — the three real captions behind the onboarding tone step.
 *
 * ONE base caption is generated from the user's actual brand profile, then
 * rewritten into the other voices. That is the design, not an optimisation:
 * three independently generated captions would differ in TOPIC as well as
 * tone, and the user would end up picking a subject while believing they had
 * picked a voice.
 *
 * `fetchImpl` and `rewrite` are injected so the failure contract is testable
 * without a network — the same reason the caption fan-out lives in its own
 * module rather than inside the component that calls it.
 */

import { presetToneInstruction } from '@/lib/tone/tone-instructions';
import {
  BRAND_TONE_OPTION_ID,
  TONE_PRESETS,
  type TonePreset,
  contrastPresetsFor,
} from '@/lib/tone/tone-presets';
import { rewriteCaption } from '@/services/media/caption-rewrite';

/**
 * Exactly the `generate-sample-post` request body (see
 * supabase/functions/generate-sample-post/request-contract.ts). Declared here
 * rather than imported from the onboarding component tree so this module stays
 * a leaf — nothing under src/lib should depend on a step component.
 */
export interface ToneSampleBrandInput {
  brandTone: string;
  businessOverview: string;
  targetAudience: string;
  businessType: string;
  companyName: string;
  marketPositioning: string;
  brandPersonality: string;
  brandColors?: { primary: string; secondary: string; accent: string };
  competitors: string[];
}

export interface ToneSampleOption {
  /** A `TonePresetId`, or `BRAND_TONE_OPTION_ID` for the scanned brand voice. */
  id: string;
  label: string;
  blurb: string;
  caption: string;
  /** True only for the card generated from the tone read off the user's site. */
  fromWebsite: boolean;
}

export interface ToneSamples {
  base: string;
  options: ToneSampleOption[];
}

/** The step's localStorage cache, keyed by the exact request that produced it. */
export interface ToneSamplesCache {
  requestSnapshot: string;
  base: string;
  options: ToneSampleOption[];
}

export interface GenerateToneSamplesDeps {
  fetchImpl?: typeof fetch;
  rewrite?: typeof rewriteCaption;
  supabaseUrl?: string;
  timeoutMs?: number;
}

/**
 * Generous relative to Ready's 20s single-shot budget because this is a base
 * generation PLUS two rewrites. Onboarding must never hang on a model, so the
 * whole batch is bounded and the step degrades rather than blocks.
 */
export const TONE_SAMPLES_TIMEOUT_MS = 30_000;

export class ToneSampleError extends Error {
  readonly name = 'ToneSampleError';
}

/**
 * A rejection that fires on abort or timeout, plus the cleanup that stops its
 * timer once the real work has settled. Both races share ONE of these so a
 * successful run leaves no pending timer behind.
 */
function createDeadline(
  timeoutMs: number,
  signal: AbortSignal,
): { promise: Promise<never>; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let onAbort: (() => void) | null = null;
  const promise = new Promise<never>((_resolve, reject) => {
    onAbort = () => {
      reject(new ToneSampleError('Tone sample generation was cancelled'));
    };
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
    timer = setTimeout(() => {
      reject(new ToneSampleError('Tone sample generation timed out'));
    }, timeoutMs);
  });
  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    if (onAbort) signal.removeEventListener('abort', onAbort);
  };
  // Both consumers are `Promise.race`, which attaches its own handler — this
  // one only guarantees a post-cancel rejection is never unhandled.
  promise.catch(() => undefined);
  return { promise, cancel };
}

async function generateBaseCaption(args: {
  brand: ToneSampleBrandInput;
  accessToken: string;
  fetchImpl: typeof fetch;
  supabaseUrl: string;
  signal: AbortSignal;
}): Promise<string> {
  const { brand, accessToken, fetchImpl, supabaseUrl, signal } = args;
  const response = await fetchImpl(`${supabaseUrl}/functions/v1/generate-sample-post`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(brand),
    signal,
  });
  if (!response.ok) {
    throw new ToneSampleError(`Sample generation failed: ${response.status}`);
  }
  const payload = (await response.json()) as { caption?: unknown };
  const caption = typeof payload.caption === 'string' ? payload.caption.trim() : '';
  if (!caption) {
    throw new ToneSampleError('Sample generation returned no caption');
  }
  return caption;
}

/**
 * Which cards to build. With a scanned brand tone the base caption IS the
 * brand's card (it was generated with that tone), so it needs no rewrite and
 * costs nothing extra — and the two presets shown beside it are the ones the
 * brand tone is NOT already, so the three cards genuinely differ.
 */
export function planToneSampleCards(brandTone: string): {
  brandCard: boolean;
  presets: TonePreset[];
} {
  const trimmed = brandTone.trim();
  if (!trimmed) return { brandCard: false, presets: [...TONE_PRESETS] };
  return { brandCard: true, presets: contrastPresetsFor(trimmed) };
}

export async function generateToneSamples(args: {
  brand: ToneSampleBrandInput;
  accessToken: string;
  signal?: AbortSignal;
  deps?: GenerateToneSamplesDeps;
}): Promise<ToneSamples> {
  const { brand, accessToken, signal, deps } = args;
  if (!accessToken) {
    throw new ToneSampleError('Tone samples require an access token');
  }

  const fetchImpl = deps?.fetchImpl ?? fetch;
  const rewrite = deps?.rewrite ?? rewriteCaption;
  const supabaseUrl = deps?.supabaseUrl ?? import.meta.env.VITE_SUPABASE_URL;
  const timeoutMs = deps?.timeoutMs ?? TONE_SAMPLES_TIMEOUT_MS;

  const controller = new AbortController();
  const abortOuter = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', abortOuter, { once: true });
  }
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const deadline = createDeadline(timeoutMs, controller.signal);

  try {
    const base = await Promise.race([
      generateBaseCaption({
        brand,
        accessToken,
        fetchImpl,
        supabaseUrl,
        signal: controller.signal,
      }),
      deadline.promise,
    ]);

    const plan = planToneSampleCards(brand.brandTone);
    const options: ToneSampleOption[] = [];

    if (plan.brandCard) {
      options.push({
        id: BRAND_TONE_OPTION_ID,
        label: 'Your voice',
        blurb: brand.brandTone.trim(),
        caption: base,
        fromWebsite: true,
      });
    }

    // Partial failure drops only its own card. Throwing away two good captions
    // because a sibling 502'd would turn a degraded step into a dead one, and
    // onboarding has nowhere to send the user from there.
    const rewrites = await Promise.race([
      Promise.all(
        plan.presets.map(async (preset) => {
          const instruction = presetToneInstruction(preset.id);
          if (!instruction) return null;
          try {
            const caption = await rewrite({ original: base, instruction, accessToken });
            const trimmed = caption.trim();
            return trimmed ? { preset, caption: trimmed } : null;
          } catch {
            return null;
          }
        }),
      ),
      deadline.promise,
    ]);

    for (const result of rewrites) {
      if (!result) continue;
      options.push({
        id: result.preset.id,
        label: result.preset.label,
        blurb: result.preset.blurb,
        caption: result.caption,
        fromWebsite: false,
      });
    }

    if (options.length === 0) {
      throw new ToneSampleError('Every tone sample failed to generate');
    }

    return { base, options };
  } finally {
    clearTimeout(timer);
    deadline.cancel();
    if (signal) signal.removeEventListener('abort', abortOuter);
  }
}
